import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Upload, FileSpreadsheet, Loader2, Check, Users, Clock, Calendar, AlertCircle, ArrowRight, X, Link2, Plus, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";

const ImportRoster = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("upload"); // upload, analyzing, matching, preview, importing, done
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [rosters, setRosters] = useState([]);
  const [targetRosterId, setTargetRosterId] = useState("");
  const [error, setError] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // Match decisions: {index: existing_id or "new"}
  const [empDecisions, setEmpDecisions] = useState({});
  const [shiftDecisions, setShiftDecisions] = useState({});

  // Checkbox selections
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);

  useEffect(() => {
    fetchRosters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRosters = async () => {
    try {
      const res = await axios.get(`${API}/rosters`, { withCredentials: true });
      setRosters(res.data);
    } catch (e) { console.warn("Roster list fetch failed:", e?.response?.status || e); }
  };

  const handleFileSelect = useCallback((selectedFile) => {
    const ext = selectedFile.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls", "tsv"].includes(ext)) {
      toast.error("Please upload a CSV or Excel file (.csv, .xlsx, .xls)");
      return;
    }
    setFile(selectedFile);
    setError(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleAnalyze = async () => {
    if (!file) return;
    setStep("analyzing");
    setError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/import/analyze`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000
      });

      const data = response.data.data;
      setAnalysisData(data);
      setAnalysisId(response.data.analysis_id);

      // Auto-select all items
      setSelectedEmployees((data.employees || []).map((_, i) => i));
      setSelectedShifts((data.shifts || []).map((_, i) => i));
      setSelectedEntries((data.schedule_entries || []).map((_, i) => i));

      // Now run smart match
      await runSmartMatch(response.data.analysis_id);
    } catch (err) {
      console.error("Analysis failed:", err);
      setError(err.response?.data?.detail || "Failed to analyze file. Try a different format.");
      setStep("upload");
    }
  };

  const runSmartMatch = async (aid) => {
    try {
      const matchRes = await axios.post(`${API}/import/smart-match`, {
        analysis_id: aid
      }, { withCredentials: true });
      
      setMatchData(matchRes.data);

      // Auto-set decisions based on matches
      const empDec = {};
      (matchRes.data.employee_matches || []).forEach((m) => {
        if (m.match_type === "exact") {
          empDec[m.extracted_index] = m.matched_employee_id;
        } else {
          empDec[m.extracted_index] = "new";
        }
      });
      setEmpDecisions(empDec);

      const shiftDec = {};
      (matchRes.data.shift_matches || []).forEach((m) => {
        if (m.match_type === "exact") {
          shiftDec[m.extracted_index] = m.matched_shift_id;
        } else {
          shiftDec[m.extracted_index] = "new";
        }
      });
      setShiftDecisions(shiftDec);

      setStep("matching");
    } catch (err) {
      console.error("Smart match failed:", err);
      // Fall through to preview without matching
      setStep("preview");
    }
  };

  const toggleEmployee = (idx) => {
    setSelectedEmployees(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };
  const toggleShift = (idx) => {
    setSelectedShifts(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };
  const toggleEntry = (idx) => {
    setSelectedEntries(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };
  const toggleAllInCategory = (category) => {
    const items = analysisData?.[category] || [];
    const setter = category === "employees" ? setSelectedEmployees
      : category === "shifts" ? setSelectedShifts : setSelectedEntries;
    const current = category === "employees" ? selectedEmployees
      : category === "shifts" ? selectedShifts : selectedEntries;
    setter(current.length === items.length ? [] : items.map((_, i) => i));
  };

  const handleImport = async () => {
    if (!analysisId) return;
    setStep("importing");

    try {
      const response = await axios.post(`${API}/import/apply`, {
        analysis_id: analysisId,
        import_employees: selectedEmployees,
        import_shifts: selectedShifts,
        import_entries: selectedEntries,
        target_roster_id: targetRosterId || null,
        employee_matches: empDecisions,
        shift_matches: shiftDecisions
      }, { withCredentials: true });

      setImportResult(response.data);
      setStep("done");
      toast.success("Import complete!");
    } catch (err) {
      console.error("Import failed:", err);
      setError(err.response?.data?.detail || "Import failed");
      setStep("preview");
      toast.error("Import failed");
    }
  };

  const reset = () => {
    setStep("upload");
    setFile(null);
    setAnalysisData(null);
    setAnalysisId(null);
    setMatchData(null);
    setEmpDecisions({});
    setShiftDecisions({});
    setSelectedEmployees([]);
    setSelectedShifts([]);
    setSelectedEntries([]);
    setError(null);
    setImportResult(null);
    setTargetRosterId("");
  };

  const totalSelected = selectedEmployees.length + selectedShifts.length + selectedEntries.length;

  const stepLabels = ["Upload", "Analyze", "Match", "Select", "Import"];
  const stepMap = { upload: 0, analyzing: 1, matching: 2, preview: 3, importing: 4, done: 4 };
  const currentStepIdx = stepMap[step] ?? 0;

  const getMatchBadge = (matchType, confidence) => {
    if (matchType === "exact") return { bg: "bg-emerald-50", text: "text-emerald-700", label: `Match ${confidence}%` };
    if (matchType === "partial") return { bg: "bg-amber-50", text: "text-amber-700", label: `Partial ${confidence}%` };
    return { bg: "bg-zinc-100 dark:bg-zinc-800", text: "text-zinc-500 dark:text-zinc-400", label: "No match" };
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="import-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Import Roster</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Upload a roster from Excel or CSV and let AI extract the data
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2">
          {stepLabels.map((label, idx) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                idx <= currentStepIdx ? 'bg-[#002FA7] text-white' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-500'
              }`}>
                {idx < currentStepIdx ? <Check className="w-3.5 h-3.5" /> : idx + 1}
              </div>
              <span className={`text-xs font-medium ${idx <= currentStepIdx ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'}`}>
                {label}
              </span>
              {idx < 4 && <div className={`w-6 h-px ${idx < currentStepIdx ? 'bg-[#002FA7]' : 'bg-zinc-200'}`} />}
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-600 mt-0.5">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 1: Upload */}
        {step === "upload" && (
          <div className="space-y-4">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`bg-white dark:bg-zinc-900 border-2 border-dashed p-12 text-center transition-colors cursor-pointer ${
                dragOver ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-zinc-300 hover:border-zinc-400'
              }`}
              onClick={() => document.getElementById("file-input").click()}
              data-testid="drop-zone"
            >
              <input id="file-input" type="file" accept=".csv,.xlsx,.xls,.tsv" className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                data-testid="file-input" />
              <div className="w-14 h-14 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-7 h-7 text-zinc-400 dark:text-zinc-500" />
              </div>
              <h3 className="text-base font-medium text-zinc-900 dark:text-zinc-100 mb-1">
                {file ? file.name : "Drop your roster file here"}
              </h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {file ? `${(file.size / 1024).toFixed(1)} KB — Ready to analyze` : "Supports CSV, Excel (.xlsx, .xls), TSV"}
              </p>
            </div>
            {file && (
              <div className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-800 p-4 rounded">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-[#002FA7]" />
                  <div>
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{file.name}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setFile(null); setError(null); }}
                    data-testid="remove-file-btn">Remove</Button>
                  <Button onClick={handleAnalyze} className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                    data-testid="analyze-btn">Analyze with AI</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Analyzing */}
        {step === "analyzing" && (
          <div className="bg-white dark:bg-zinc-900 border-2 border-[#002FA7] p-10 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 tracing-beam"></div>
            <Loader2 className="w-10 h-10 text-[#002FA7] animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-1">Analyzing your roster...</h3>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">AI is extracting staff, shifts, and schedule data</p>
          </div>
        )}

        {/* Step 3: Smart Match */}
        {step === "matching" && matchData && (
          <div className="space-y-6" data-testid="smart-match-step">
            <div className="bg-blue-50 border border-blue-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Link2 className="w-4 h-4 text-blue-600" />
                <p className="text-sm font-medium text-blue-800">Smart Match</p>
              </div>
              <p className="text-sm text-blue-700">
                We compared the imported data against your existing employees and shifts. 
                Review matches below — link to existing records or create new ones.
              </p>
            </div>

            {/* Employee Matches */}
            {matchData.employee_matches?.length > 0 && (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="employee-match-section">
                <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#002FA7]" />
                  <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">Employee Matching</h2>
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">
                    ({matchData.employee_matches.filter(m => m.match_type !== "none").length}/{matchData.employee_matches.length} matched)
                  </span>
                </div>
                <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {matchData.employee_matches.map((match) => {
                    const badge = getMatchBadge(match.match_type, match.confidence);
                    const decision = empDecisions[match.extracted_index];
                    return (
                      <div key={match.extracted_index} className="p-4" data-testid={`emp-match-${match.extracted_index}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{match.extracted_name}</p>
                              {match.extracted_email && (
                                <span className="text-xs text-zinc-500 dark:text-zinc-400">{match.extracted_email}</span>
                              )}
                              <span className={`text-xs font-medium px-2 py-0.5 rounded ${badge.bg} ${badge.text}`}>
                                {badge.label}
                              </span>
                            </div>
                            {match.match_type !== "none" && (
                              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                Suggested match: <span className="font-medium text-zinc-700 dark:text-zinc-300">{match.matched_name}</span>
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0 w-56">
                            <Select
                              value={decision || "new"}
                              onValueChange={(val) => setEmpDecisions(prev => ({ ...prev, [match.extracted_index]: val }))}
                            >
                              <SelectTrigger className="text-sm h-9" data-testid={`emp-decision-${match.extracted_index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">
                                  <div className="flex items-center gap-1.5">
                                    <Plus className="w-3 h-3" />
                                    Create New Employee
                                  </div>
                                </SelectItem>
                                {(match.alternatives || []).map((alt) => (
                                  <SelectItem key={alt.employee_id} value={alt.employee_id}>
                                    <div className="flex items-center gap-1.5">
                                      <Link2 className="w-3 h-3" />
                                      {alt.name} {alt.email ? `(${alt.email})` : ""}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Shift Matches */}
            {matchData.shift_matches?.length > 0 && (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="shift-match-section">
                <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#002FA7]" />
                  <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">Shift Matching</h2>
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">
                    ({matchData.shift_matches.filter(m => m.match_type !== "none").length}/{matchData.shift_matches.length} matched)
                  </span>
                </div>
                <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {matchData.shift_matches.map((match) => {
                    const badge = getMatchBadge(match.match_type, match.confidence);
                    const decision = shiftDecisions[match.extracted_index];
                    return (
                      <div key={match.extracted_index} className="p-4" data-testid={`shift-match-${match.extracted_index}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{match.extracted_name}</p>
                              <span className="text-xs text-zinc-500 dark:text-zinc-400">
                                {match.extracted_start} — {match.extracted_end}
                              </span>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded ${badge.bg} ${badge.text}`}>
                                {badge.label}
                              </span>
                            </div>
                            {match.match_type !== "none" && (
                              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                Suggested: <span className="font-medium text-zinc-700 dark:text-zinc-300">{match.matched_name}</span>
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0 w-56">
                            <Select
                              value={decision || "new"}
                              onValueChange={(val) => setShiftDecisions(prev => ({ ...prev, [match.extracted_index]: val }))}
                            >
                              <SelectTrigger className="text-sm h-9" data-testid={`shift-decision-${match.extracted_index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">
                                  <div className="flex items-center gap-1.5">
                                    <Plus className="w-3 h-3" />
                                    Create New Shift
                                  </div>
                                </SelectItem>
                                {(match.alternatives || []).map((alt) => (
                                  <SelectItem key={alt.shift_id} value={alt.shift_id}>
                                    <div className="flex items-center gap-1.5">
                                      <Link2 className="w-3 h-3" />
                                      {alt.name} ({alt.start_time}—{alt.end_time})
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Summary & Continue */}
            <div className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-800 p-4">
              <div>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  <span className="font-medium text-emerald-700">
                    {Object.values(empDecisions).filter(v => v !== "new").length} employees
                  </span> linked, {" "}
                  <span className="font-medium text-[#002FA7]">
                    {Object.values(empDecisions).filter(v => v === "new").length} new
                  </span>
                  {" | "}
                  <span className="font-medium text-emerald-700">
                    {Object.values(shiftDecisions).filter(v => v !== "new").length} shifts
                  </span> linked, {" "}
                  <span className="font-medium text-[#002FA7]">
                    {Object.values(shiftDecisions).filter(v => v === "new").length} new
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={reset} data-testid="cancel-match-btn">Cancel</Button>
                <Button onClick={() => setStep("preview")}
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="confirm-matches-btn">
                  Continue to Review <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Preview & Select */}
        {step === "preview" && analysisData && (
          <div className="space-y-6">
            {analysisData.summary && (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 mb-2">Analysis Summary</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{analysisData.employees?.length || 0}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">Employees</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{analysisData.shifts?.length || 0}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">Shift types</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{analysisData.schedule_entries?.length || 0}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">Schedule entries</p>
                  </div>
                </div>
                {analysisData.summary.notes && (
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800">{analysisData.summary.notes}</p>
                )}
              </div>
            )}

            {/* Match summary banner */}
            {matchData && (
              <div className="bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-emerald-600" />
                <p className="text-sm text-emerald-800">
                  Smart Match: {Object.values(empDecisions).filter(v => v !== "new").length} employees and {Object.values(shiftDecisions).filter(v => v !== "new").length} shifts linked to existing records.
                  <button onClick={() => setStep("matching")} className="underline ml-1 font-medium" data-testid="edit-matches-btn">
                    Edit matches
                  </button>
                </p>
              </div>
            )}

            {/* Employees */}
            {analysisData.employees?.length > 0 && (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="import-employees-section">
                <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#002FA7]" />
                    <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">Employees</h2>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">({selectedEmployees.length}/{analysisData.employees.length})</span>
                  </div>
                  <button onClick={() => toggleAllInCategory("employees")}
                    className="text-xs text-[#002FA7] hover:underline" data-testid="toggle-all-employees">
                    {selectedEmployees.length === analysisData.employees.length ? "Deselect All" : "Select All"}
                  </button>
                </div>
                <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {analysisData.employees.map((emp, idx) => {
                    const decision = empDecisions[idx];
                    const isLinked = decision && decision !== "new";
                    const matchInfo = matchData?.employee_matches?.find(m => m.extracted_index === idx);
                    return (
                      <label key={idx} className="flex items-center gap-3 p-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-colors">
                        <Checkbox checked={selectedEmployees.includes(idx)}
                          onCheckedChange={() => toggleEmployee(idx)} data-testid={`check-emp-${idx}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{emp.name}</p>
                            {isLinked && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 flex items-center gap-1">
                                <Link2 className="w-3 h-3" />
                                Linked to {matchInfo?.matched_name || "existing"}
                              </span>
                            )}
                            {!isLinked && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-700 flex items-center gap-1">
                                <Plus className="w-3 h-3" /> New
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            {emp.email && <span className="text-xs text-zinc-500 dark:text-zinc-400">{emp.email}</span>}
                            {emp.max_hours_per_week && <span className="text-xs text-zinc-500 dark:text-zinc-400">{emp.max_hours_per_week}h/week</span>}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Shifts */}
            {analysisData.shifts?.length > 0 && (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="import-shifts-section">
                <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#002FA7]" />
                    <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">Shift Types</h2>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">({selectedShifts.length}/{analysisData.shifts.length})</span>
                  </div>
                  <button onClick={() => toggleAllInCategory("shifts")}
                    className="text-xs text-[#002FA7] hover:underline" data-testid="toggle-all-shifts">
                    {selectedShifts.length === analysisData.shifts.length ? "Deselect All" : "Select All"}
                  </button>
                </div>
                <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {analysisData.shifts.map((shift, idx) => {
                    const decision = shiftDecisions[idx];
                    const isLinked = decision && decision !== "new";
                    const matchInfo = matchData?.shift_matches?.find(m => m.extracted_index === idx);
                    return (
                      <label key={idx} className="flex items-center gap-3 p-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-colors">
                        <Checkbox checked={selectedShifts.includes(idx)}
                          onCheckedChange={() => toggleShift(idx)} data-testid={`check-shift-${idx}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{shift.name}</p>
                            {isLinked && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 flex items-center gap-1">
                                <Link2 className="w-3 h-3" /> Linked to {matchInfo?.matched_name || "existing"}
                              </span>
                            )}
                            {!isLinked && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-700 flex items-center gap-1">
                                <Plus className="w-3 h-3" /> New
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400">{shift.start_time} — {shift.end_time}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Schedule Entries */}
            {analysisData.schedule_entries?.length > 0 && (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid="import-entries-section">
                <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#002FA7]" />
                    <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">Schedule Entries</h2>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">({selectedEntries.length}/{analysisData.schedule_entries.length})</span>
                  </div>
                  <button onClick={() => toggleAllInCategory("schedule_entries")}
                    className="text-xs text-[#002FA7] hover:underline" data-testid="toggle-all-entries">
                    {selectedEntries.length === analysisData.schedule_entries.length ? "Deselect All" : "Select All"}
                  </button>
                </div>
                {selectedEntries.length > 0 && (
                  <div className="p-3 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
                    <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 block mb-1">Import entries into roster</label>
                    <Select value={targetRosterId} onValueChange={setTargetRosterId}>
                      <SelectTrigger className="bg-white dark:bg-zinc-900" data-testid="target-roster-select">
                        <SelectValue placeholder="Select a roster" />
                      </SelectTrigger>
                      <SelectContent>
                        {rosters.map((r) => (
                          <SelectItem key={r.roster_id} value={r.roster_id}>
                            {r.name} ({r.start_date} — {r.end_date})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="max-h-[300px] overflow-y-auto divide-y divide-zinc-100 dark:divide-zinc-800">
                  {analysisData.schedule_entries.map((entry, idx) => (
                    <label key={idx} className="flex items-center gap-3 p-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-colors">
                      <Checkbox checked={selectedEntries.includes(idx)}
                        onCheckedChange={() => toggleEntry(idx)} data-testid={`check-entry-${idx}`} />
                      <div className="flex-1 grid grid-cols-4 gap-2 text-sm">
                        <span className="text-zinc-900 dark:text-zinc-100 font-medium">{entry.employee_name}</span>
                        <span className="text-zinc-600 dark:text-zinc-400">{entry.shift_name}</span>
                        <span className="text-zinc-600 dark:text-zinc-400 tabular-nums">{entry.date}</span>
                        <span className="text-zinc-500 dark:text-zinc-400 text-xs">{entry.hours ? `${entry.hours}h` : ""}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Import Actions */}
            <div className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-800 p-4">
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                <span className="font-medium text-zinc-900 dark:text-zinc-100">{totalSelected}</span> items selected
              </p>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={reset} data-testid="cancel-import-btn">Cancel</Button>
                <Button onClick={handleImport}
                  disabled={totalSelected === 0 || (selectedEntries.length > 0 && !targetRosterId)}
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="confirm-import-btn">
                  Import Selected <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Importing */}
        {step === "importing" && (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-10 text-center">
            <Loader2 className="w-8 h-8 text-[#002FA7] animate-spin mx-auto mb-4" />
            <p className="text-base font-medium text-zinc-900 dark:text-zinc-100">Importing data...</p>
          </div>
        )}

        {/* Step 6: Done */}
        {step === "done" && importResult && (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-8 space-y-6" data-testid="import-results">
            <div className="text-center">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-1">Import Complete</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 dark:border-zinc-800">
              <div className="bg-white dark:bg-zinc-900 p-4 text-center">
                <p className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{importResult.employees_created}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">New Employees</p>
              </div>
              <div className="bg-white dark:bg-zinc-900 p-4 text-center">
                <p className="text-2xl font-bold tabular-nums text-emerald-700">{importResult.employees_linked || 0}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Linked Employees</p>
              </div>
              <div className="bg-white dark:bg-zinc-900 p-4 text-center">
                <p className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{importResult.shifts_created}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">New Shifts</p>
              </div>
              <div className="bg-white dark:bg-zinc-900 p-4 text-center">
                <p className="text-2xl font-bold tabular-nums text-emerald-700">{importResult.shifts_linked || 0}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Linked Shifts</p>
              </div>
            </div>
            {importResult.entries_created > 0 && (
              <div className="bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-800 p-4 text-center">
                <p className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{importResult.entries_created}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Schedule Entries Imported</p>
              </div>
            )}
            {importResult.errors?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 p-4">
                <p className="text-sm font-medium text-amber-800 mb-2">Some items had issues:</p>
                <ul className="space-y-1">
                  {importResult.errors.map((err, idx) => (
                    <li key={idx} className="text-xs text-amber-700">- {err}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={reset} data-testid="import-another-btn">Import Another</Button>
              <Button onClick={() => navigate("/employees")}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white" data-testid="go-to-employees-btn">
                View Employees
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ImportRoster;
